/**
 * MilestoneToastEngine — invisible background component that monitors the
 * writer's project state and fires a single non-blocking amber toast the
 * first time each milestone is reached. Milestones are tracked in
 * localStorage so they fire at most once per browser/user.
 *
 * Milestones watched:
 *  1. first_chapter_written  — at least one chapter has >50 words
 *  2. three_chapters_drafted — 3+ chapters have content
 *  3. five_chapters_drafted  — 5+ chapters have content (1/4 of a novel!)
 *  4. ten_k_words            — combined word count ≥ 10,000
 *  5. first_character_added  — at least one character in the Bible
 *
 * Mount once inside AppShell (renders nothing).
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';

const STORAGE_KEY = 'cr_milestones_seen';

const MILESTONES = [
  {
    id: 'first_chapter_written',
    check: ({ chapters }) => chapters.some((c) => (c.words || 0) > 50),
    title: 'First chapter written',
    description: 'You put words on the page — that\'s the hardest part. Keep going.',
  },
  {
    id: 'three_chapters_drafted',
    check: ({ chapters }) => chapters.filter((c) => (c.words || 0) > 50).length >= 3,
    title: 'Three chapters in',
    description: 'Momentum is building. Your story is starting to breathe.',
  },
  {
    id: 'five_chapters_drafted',
    check: ({ chapters }) => chapters.filter((c) => (c.words || 0) > 50).length >= 5,
    title: 'Halfway through an act',
    description: 'Five chapters done — you\'re a quarter of the way through a novel draft!',
  },
  {
    id: 'ten_k_words',
    check: ({ chapters }) =>
      chapters.reduce((sum, c) => sum + (c.words || 0), 0) >= 10000,
    title: '10,000 words reached',
    description: 'A short story in the bank. You\'re thinking like a novelist now.',
  },
  {
    id: 'first_character_added',
    check: ({ characters }) => Array.isArray(characters) && characters.length >= 1,
    title: 'Character Bible started',
    description: 'A story lives through its characters. Your cast is taking shape.',
  },
];

function loadSeen() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveSeen(set) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

export default function MilestoneToastEngine() {
  const { chapters, characters } = usePW();   // characters is a top-level slice, not on project
  const seenRef = useRef(loadSeen());

  useEffect(() => {
    if (!chapters) return;
    const seen = seenRef.current;
    let changed = false;

    MILESTONES.forEach(({ id, check, title, description }) => {
      if (seen.has(id)) return;
      try {
        if (check({ chapters, characters })) {
          seen.add(id);
          changed = true;
          // Amber non-blocking toast, auto-dismisses after 5 s
          toast(title, {
            description,
            duration: 5000,
            position: 'bottom-right',
            style: {
              background: 'hsl(var(--background))',
              border: '1px solid #d97706',
              color: 'hsl(var(--foreground))',
            },
            icon: '✦',
          });
        }
      } catch { /* never crash the writer's session */ }
    });

    if (changed) saveSeen(seen);
  }, [chapters, characters]);

  return null; // renders nothing
}
